import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

import { safeReturnTo } from '@/lib/settings/settings-return';

/** What a door may pass in the link's state to name the page it opened the panel from. */
export const OPENED_FROM_HOME = { openedFrom: '/' } as const;

const PANEL_ROUTE = '/campaigns/ads/new';
const DOOR_PARAMS = ['create', 'origin', 'returnTo', 'connected'];

let lastPage: string | null = null;
let pushedOver: string | null = null;

export function isPanelRoute(pathname: string): boolean {
  return pathname === PANEL_ROUTE || pathname.startsWith(`${PANEL_ROUTE}/`);
}

/** The address a person can be sent back to: inside the app, never the panel, Settings or sign-in, and without a door's own dialog params. */
export function openerAddress(address: string | null | undefined): string | null {
  const safe = safeReturnTo(address);
  if (!safe) return null;
  const [path, query = ''] = safe.split('?');
  if (isPanelRoute(path)) return null;
  const params = new URLSearchParams(query);
  for (const name of DOOR_PARAMS) params.delete(name);
  const rest = params.toString();
  return rest ? `${path}?${rest}` : path;
}

/** Notes the page a person is on, so the panel can return there whichever door opened it. */
export function useRememberOpenedFrom(): void {
  const { pathname, search } = useLocation();
  const navigationType = useNavigationType();
  useEffect(() => {
    if (isPanelRoute(pathname)) {
      pushedOver = navigationType === 'PUSH' && pushedOver === null ? lastPage : null;
      return;
    }
    pushedOver = null;
    const page = openerAddress(`${pathname}${search}`);
    if (page) lastPage = page;
  }, [pathname, search, navigationType]);
}

/** The state a door passes so the panel knows the page it was opened from. */
export function openedFromState(page: { pathname: string; search?: string }): {
  openedFrom: string;
} {
  return { openedFrom: `${page.pathname}${page.search ?? ''}` };
}

/** The page the panel was opened from: the door's own word first, then the last page seen. */
export function resolveOpenedFrom(state: unknown): string | null {
  const named = (state as { openedFrom?: unknown } | null)?.openedFrom;
  return (typeof named === 'string' ? openerAddress(named) : null) ?? lastPage;
}

/** True when the panel was pushed straight on top of `opener` in this tab, so going back lands there. */
export function panelSitsOn(opener: string | null): boolean {
  return opener !== null && pushedOver === opener;
}

export function resetOpenedFrom(): void {
  lastPage = null;
  pushedOver = null;
}

export function pathOf(address: string): string {
  return address.split('?')[0];
}
